import { supabase } from '@/integrations/supabase/client';
import type {
  ContentMediaStoragePort,
  UploadImageResult,
} from '../ports/contentStorage.port';

const BUCKET_NAME = 'campaign-content';

export const getImageDimensions = (file: File): Promise<{ width: number; height: number; size: number }> => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof Image === 'undefined') {
      resolve({ width: 0, height: 0, size: file.size });
      return;
    }
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
        size: file.size,
      });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve({
        width: 0,
        height: 0,
        size: file.size,
      });
    };
    img.src = URL.createObjectURL(file);
  });
};

export class SupabaseContentStorage implements ContentMediaStoragePort {
  async uploadImage({
    file,
    organizationId,
    campaignId,
    folderId,
  }: {
    file: File;
    organizationId: string;
    campaignId: string;
    folderId?: string;
  }): Promise<UploadImageResult> {
    const fileExt = file.name.split('.').pop() || 'jpg';
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 8);
    const fileName = `${timestamp}_${randomId}.${fileExt}`;

    const filePath = folderId
      ? `${organizationId}/${campaignId}/${folderId}/${fileName}`
      : `${organizationId}/${campaignId}/root/${fileName}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    return {
      filePath: uploadData.path,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || `image/${fileExt}`,
    };
  }

  async deleteImage(filePath: string): Promise<void> {
    const { error } = await supabase.storage.from(BUCKET_NAME).remove([filePath]);
    if (error) {
      throw new Error(`Storage delete failed: ${error.message}`);
    }
  }

  async getSignedUrl(filePath: string, expiresInSeconds = 3600): Promise<string> {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .createSignedUrl(filePath, expiresInSeconds);

    if (error) {
      throw new Error(`Signed URL creation failed: ${error.message}`);
    }

    return data.signedUrl;
  }
}

export const supabaseContentStorage = new SupabaseContentStorage();
